import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Brain, Workflow, ShieldCheck, GraduationCap } from "lucide-react";
import HeroBackground from "@/components/HeroBackground";
import FinalCTA from "@/components/FinalCTA";
import SectionHeading from "@/components/SectionHeading";
import { useT } from "@/i18n";

const ICONS = [Brain, Brain, ShieldCheck, Workflow, Brain, Workflow, Workflow, ShieldCheck, GraduationCap, Brain];

export default function AITransformation() {
  const t = useT();
  return (
    <div>
      <section className="relative overflow-hidden">
        <HeroBackground />
        <div className="relative nx-container pt-16 md:pt-24 pb-20 md:pb-28">
          <div className="nx-mono text-xs uppercase tracking-[0.32em] text-[#38D8FF] mb-5 nx-fade-in">
            / {t.aiPage.eyebrow}
          </div>
          <h1 className="nx-heading text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight max-w-4xl tracking-tight nx-fade-in">
            <span className="nx-gradient-text">{t.aiPage.title1}</span> {t.aiPage.title2}
          </h1>
          <p className="mt-6 max-w-2xl text-lg md:text-xl text-white/75 leading-relaxed nx-fade-in">
            {t.aiPage.lede}
          </p>
          <div className="mt-10 flex flex-wrap gap-4 nx-fade-in">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-md bg-[#0057FF] hover:bg-[#2F80FF] text-white text-sm font-medium nx-cta-glow"
            >
              {t.aiPage.startBtn} <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-md border border-white/20 hover:border-[#38D8FF] text-white/85 text-sm font-medium"
            >
              {t.aiPage.allServicesBtn}
            </Link>
          </div>
        </div>
      </section>

      <section className="nx-container py-24 md:py-32">
        <SectionHeading
          eyebrow={t.aiPage.servicesEyebrow}
          title={t.aiPage.servicesTitle}
          lede={t.aiPage.servicesLede}
        />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {t.aiPage.services.map((s, i) => {
            const Icon = ICONS[i] || Brain;
            return (
              <div key={s.title} className="nx-card rounded-2xl p-7 group hover:-translate-y-1 transition-transform">
                <div className="flex items-center justify-between mb-6">
                  <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#0057FF]/40 to-[#38D8FF]/20 border border-white/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#38D8FF]" />
                  </div>
                  <span className="nx-mono text-xs text-white/40">/ 0{i + 1}</span>
                </div>
                <h3 className="nx-heading text-lg font-semibold">{s.title}</h3>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#050F22]/60">
        <div className="nx-container py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <SectionHeading
              eyebrow={t.aiPage.approachEyebrow}
              title={t.aiPage.approachTitle}
              lede={t.aiPage.approachLede}
            />
          </div>
          <div className="lg:col-span-5 space-y-3">
            {t.aiPage.phases.map((p) => (
              <div key={p.step} className="nx-card rounded-xl p-5 flex items-start gap-5">
                <div className="nx-mono text-[#38D8FF] text-sm shrink-0">{p.step}</div>
                <div>
                  <div className="nx-heading text-base font-semibold">{p.title}</div>
                  <div className="text-sm text-white/65 mt-1">{p.desc}</div>
                </div>
              </div>
            ))}
            <Link
              to="/contact"
              className="mt-4 inline-flex items-center gap-2 text-sm text-[#38D8FF] hover:text-white transition"
            >
              {t.aiPage.talkLink} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
